import { DatePipe } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Inject,
} from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { BehaviorSubject, filter, Observable, of, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import jspreadsheet from 'jspreadsheet';
import { v4 as uuid } from 'uuid';

import { ConfirmDialogComponent } from '@shared/components';
import { StaticMessage } from '@shared/enums';
import { DataType, SelectionItem } from '@shared/models';
import { BaseDialogComponent } from '@shared/models/component';
import {
  CustomData,
  Activity,
  ActivityGroup,
  Location,
} from '@shared/models/project-setup';
import {
  Constants,
  convertToDateTimeWithoutTimeZoneSuffix,
  getStaticMessage,
  isDateBeforeEpoch,
  normalizeTimeFormat,
} from '@shared/utils';
import { ColumnCoords } from '@dpr-details/enums';
import { ImportActivitiesData } from '@dpr-details/models';
import { DprActivityGridData } from '@dpr-details/models/grid';
import {
  DprActivityCopyService,
  ImportDprActivityValidation,
} from '@dpr-details/services';
import {
  buildCustomDataColumnName,
  dateRegex,
  dateTimeRegex,
  findByValue,
  formatTimeToDisplayString,
  parseCustomDataInfoFromColumnName,
  readOnlyPlaceholder,
  readOnlyBackgroundColor,
} from '@dpr-details/utils';

interface CellConfig {
  type: 'text' | 'dropdown';
  source?: string[] | SelectionItem[] | jspreadsheet.DropdownItem[];
  autoCasting?: boolean;
  autocomplete?: boolean;
  render?: jspreadsheet.Column['render'];
}

interface SpreadsheetRecord {
  x: number;
  y: number;
  value?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-dpr-import-activities-dialog',
  templateUrl: './dpr-import-activities-dialog.component.html',
  styleUrls: ['./dpr-import-activities-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprImportActivitiesDialogComponent
  extends BaseDialogComponent
  implements AfterViewInit
{
  @ViewChild('jspreadsheet') jspreadsheet: ElementRef;
  worksheets: jspreadsheet.worksheetInstance[];

  hasValidationErrors$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  exceededNumberOfColumnsErrors$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  mandatoryRecords$: BehaviorSubject<Set<SpreadsheetRecord>> =
    new BehaviorSubject<Set<SpreadsheetRecord>>(new Set());

  highlightedCellsError = $localize`:@@dprDetails.dprImportActivitiesDialog.errHighlightedCellsError:The highlighted cells are (---).`;
  exceededNumberOfColumnsError = $localize`:@@dprDetails.dprImportActivitiesDialog.errExceededNumberOfColumns:Number of pasted columns exceeds configured columns.`;
  invalidLabel = $localize`:@@dprDetails.dprImportActivitiesDialog.lblInvalidLabel:invalid`;
  mandatoryLabel = $localize`:@@dprDetails.dprImportActivitiesDialog.lblMandatoryLabel:mandatory`;

  StaticMessage = StaticMessage;

  private normalizedPasteData: string[][] = [];
  private cellsToRestoreAfterFill: SpreadsheetRecord[] = []; // Tracks readonly cells that need to be emptied after fill
  private isCellUpdateInProgress = false; // Flag used to prevent validation loops and fill-handle interference
  private resizeObserver!: ResizeObserver;
  private mutationObserver!: MutationObserver;
  private emptyDropdownItem: jspreadsheet.DropdownItem = {
    id: 0,
    name: $localize`:@@dprDetails.dprImportActivitiesDialog.optNoResult:No result`,
    disabled: true,
  };
  preprocessorFunctions = new Map<ColumnCoords, [(value?: string) => string]>([
    [ColumnCoords.StartColumn, [formatTimeToDisplayString]],
    [ColumnCoords.FinishColumn, [formatTimeToDisplayString]],
  ]);

  constructor(
    public dialogRef: MatDialogRef<DprImportActivitiesDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: ImportActivitiesData,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private importDprActivityValidation: ImportDprActivityValidation,
    private dprActivityCopyService: DprActivityCopyService
  ) {
    super(dialogRef);
  }

  get getErrorMessage(): string | null {
    // Check copy-paste/fill-handle errors first
    if (this.worksheets?.[0]?.hasErrors?.()) {
      return this.importDprActivityValidation.getValidationError(
        this.highlightedCellsError,
        this.invalidLabel
      );
    }

    // Check mandatory fields error
    if (this.mandatoryRecords$.value.size > 0) {
      return this.importDprActivityValidation.getValidationError(
        this.highlightedCellsError,
        this.mandatoryLabel
      );
    }

    return null;
  }

  ngAfterViewInit(): void {
    this.initJspreadsheet();

    setTimeout(() => {
      this.observeJssInputs();
      this.observeResize();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  onInsert(): void {
    const spreadsheetData = this.worksheets[0].getData() as Record<
      string,
      any
    >[];

    if (!spreadsheetData || spreadsheetData.length === 0) {
      this.dialogRef.close();
      return;
    }

    if (!this.isMandatoryDataValid(spreadsheetData)) {
      this.hasValidationErrors$.next(true);
      return;
    }

    this.dialogRef.close(
      spreadsheetData.map((row) => this.convertRowToGridData(row))
    );
  }

  onCancel(): void {
    if (!this.isDirty()) {
      this.dialogRef.close();
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: getStaticMessage(StaticMessage.UnsavedChangesTitle),
        message: getStaticMessage(
          StaticMessage.UnsavedChangesText,
          $localize`:@@dprDetails.dprImportActivitiesDialog.txtCloseOrCancel:close/cancel`,
          $localize`:@@dprDetails.dprImportActivitiesDialog.txtAndDiscardChanges:and discard the changes`
        ),
        buttonLabel: getStaticMessage(StaticMessage.ContinueButton),
      },
    });

    confirmDialog
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((val) => val),
        tap(() => {
          // Close import dialog and discard all unsaved data on Continue
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty();
  }

  save(): Observable<boolean> {
    return of(false);
  }

  private isDirty(): boolean {
    if (!this.worksheets?.[0]) {
      return false;
    }

    const spreadsheetData = this.worksheets[0].getData() as Record<
      string,
      any
    >[];

    if (!spreadsheetData || spreadsheetData.length === 0) {
      return false;
    }

    // Check if there is any non-empty row
    return spreadsheetData.some((row) => {
      const rowValues = Array.isArray(row) ? row : Object.values(row);
      return rowValues.some((cell) => cell?.toString().trim());
    });
  }

  private initJspreadsheet(): void {
    this.worksheets = jspreadsheet(this.jspreadsheet.nativeElement, {
      worksheets: [
        {
          editable: true,
          minDimensions: [6, 100],
          worksheetName: this.data.worksheetName,
          csvHeaders: true,
          allowInsertColumn: false,
          allowManualInsertColumn: false,
          allowDeleteColumn: false,
          allowRenameColumn: false,
          rowDrag: false,
          allowComments: false,
          columnDrag: false,
          columns: this.getColumns(),
          tableOverflow: true,
          // This is needed to ensure that all rows are loaded at the start,
          // so that no error is displayed when trying to copy the empty table
          virtualizationX: false,
          virtualizationY: false,
          columnSorting: false,
          columnSortingOnDblClick: false,
        },
      ],
      parseFormulas: false,
      includeHeadersOnDownload: true,
      autoIncrement: false,
      contextMenu: function (_o, _x, _y, _e, items, _section) {
        // Disable hide/show menu options
        return items.filter(
          (item) =>
            !item.title?.includes('Hide') && !item.title?.includes('Show')
        );
      },
      validations: this.importDprActivityValidation.getValidations(
        this.data,
        this.normalizedPasteData
      ),
      oninsertrow: (instance: jspreadsheet.worksheetInstance, newRow: any) =>
        this.onInsertRow(instance, newRow),
      ondeleterow: (
        instance: jspreadsheet.worksheetInstance,
        deletedRows: number[]
      ) => this.onDeleteRow(instance, deletedRows),
      onchange: (instance, _cell, x, y, newValue) =>
        this.onChange(instance, x, y, newValue),
      oncopy: (_worksheet, _selectedCells, data) => data,
      onbeforepaste: (worksheet, data, x, y) =>
        this.onBeforePaste(data, worksheet, x, y),
      onpaste: (
        instance: jspreadsheet.worksheetInstance,
        data: Record<string, any>[]
      ) => this.onPaste(instance, data),
      onbeforechanges: (
        instance: jspreadsheet.worksheetInstance,
        records: Record<string, any>[],
        origin: string
      ) =>
        this.onBeforeChanges(instance, records as SpreadsheetRecord[], origin),
      onafterchanges: (
        instance: jspreadsheet.worksheetInstance,
        records: Record<string, any>[],
        origin: string
      ) =>
        this.onAfterChanges(instance, records as SpreadsheetRecord[], origin),
      // This is needed to trim text values that are manually typed into the grid
      onbeforechange: (_worksheet, _cell, _x, _y, value) =>
        value?.toString().trim(),
    });

    jspreadsheet.shortcuts.set('ctrlKey.Z', null); // Disable undo shortcut
    jspreadsheet.shortcuts.set('ctrlKey.Y', null); // Disable redo shortcut
    jspreadsheet.shortcuts.set('Delete', () => this.onDeleteSelection());

    this.preventScrollJump();
  }

  /**
   * Prevents jspreadsheet from jumping/shifting rows when touchpad scrolling ends.
   * This fixes an issue where, after scrolling using the touchpad, the table moves
   * down by one row.
   */
  private preventScrollJump(): void {
    // Prevent unwanted scroll events from triggering cell selection
    const wheelHandler = (e: WheelEvent) => {
      e.stopPropagation();
    };

    this.jspreadsheet.nativeElement.addEventListener('wheel', wheelHandler, {
      capture: true,
    });
  }

  private getColumns(): jspreadsheet.Column[] {
    /* Dynamically adjust column width to properly fill the dialog:
     * - ideal total width of the table: 1820
     * - start and finish fields set to fixed 80
     * - comment column has with 140 more then the rest
     * - the rest of the width (1520) is split equally to all columns
     */
    const width = 1520 / (4 + this.data.inlineCustomData.length);

    const customDataColumns = this.data.inlineCustomData.map((cd) => ({
      title: cd.name,
      autoCasting: ![DataType.FreeText, DataType.Number].includes(
        cd.dataTypeId
      ),
      autocomplete:
        this.getCustomDataType(cd.dataTypeId) === 'dropdown' ? true : false,
      name: buildCustomDataColumnName(cd),
      type: this.getCustomDataType(cd.dataTypeId),
      render: this.customRenderer(cd),
      width,
      source: this.setDropdownSource(this.data.getSelectionOptions(cd)),
    }));

    for (let i = 1; i <= customDataColumns.length; i++) {
      if (
        parseCustomDataInfoFromColumnName(customDataColumns[i - 1].name)
          .dataTypeId === DataType.Datetime
      ) {
        this.preprocessorFunctions.set(ColumnCoords.CommentColumn + i, [
          this.formatDateTime.bind(this),
        ]);
        continue;
      }

      if (
        parseCustomDataInfoFromColumnName(customDataColumns[i - 1].name)
          .dataTypeId === DataType.Date
      ) {
        this.preprocessorFunctions.set(ColumnCoords.CommentColumn + i, [
          this.formatDate.bind(this),
        ]);
        continue;
      }
    }

    return [
      {
        title: getStaticMessage(StaticMessage.ActivityGroupColumn),
        name: 'ActivityGroup',
        type: 'dropdown',
        autocomplete: true,
        width,
        align: 'left',
        filterOptions: (_worksheet, _cell, _x, _y, _value, config) => {
          const activityGroupOptions = this.data
            .getActivityGroups()
            .map((x) => x.name);

          config.source = this.setDropdownSource(activityGroupOptions);
          return config;
        },
      },
      {
        title: getStaticMessage(StaticMessage.ActivityColumn),
        name: 'Activity',
        type: 'dropdown',
        autocomplete: true,
        filterOptions: (worksheet, _cell, _x, y, _value, config) =>
          this.filterActivityOptions(worksheet, y, config),
        width,
        align: 'left',
      },
      {
        title: getStaticMessage(StaticMessage.LocationColumn),
        name: 'Location',
        type: 'dropdown',
        autocomplete: true,
        filterOptions: (worksheet, _cell, _x, y, _value, config) =>
          this.filterLocationOptions(worksheet, y, config),
        width,
        align: 'left',
      },
      {
        title: getStaticMessage(StaticMessage.StartColumn),
        name: 'Start',
        type: 'text',
        width: 80,
        source: [],
      },
      {
        title: getStaticMessage(StaticMessage.FinishColumn),
        name: 'Finish',
        type: 'text',
        width: 80,
        source: [],
      },
      {
        title: getStaticMessage(StaticMessage.CommentColumn),
        name: 'Comment',
        type: 'text',
        width: width + 140,
        align: 'left',
        source: [],
      },
      ...customDataColumns,
    ];
  }

  private onChange(
    instance: jspreadsheet.worksheetInstance,
    x: number | string,
    y: number | string,
    newValue: any
  ): void {
    this.exceededNumberOfColumnsErrors$.next(false);

    const col = Number(x);
    const row = Number(y);

    // Clear validation state for row
    if (newValue !== readOnlyPlaceholder) {
      this.clearValidationState(instance);
    }

    // Skip processing for programmatic updates (fill-handle validation triggers) to prevent infinite loops
    if (this.isCellUpdateInProgress) {
      return;
    }

    switch (col) {
      case ColumnCoords.ActivityGroup:
        this.handleActivityGroupChange(instance, row);
        this.updateMilestoneFinishColumn(instance, row); // Add milestone handling
        // Re-validate Activity and Location on ActivityGroup change
        this.triggerValidationOnCell(instance, ColumnCoords.Activity, row);
        this.triggerValidationOnCell(instance, ColumnCoords.Location, row);
        break;
      case ColumnCoords.Activity:
        this.handleActivityChange(instance, row);
        this.updateMilestoneFinishColumn(instance, row); // Add milestone handling
        this.triggerValidationOnCell(instance, ColumnCoords.Location, row); // Re-validate Location on Activity change
        break;
      case ColumnCoords.StartColumn:
        this.updateMilestoneFinishColumn(instance, row); // Add start time change handling
        this.triggerValidationOnCell(instance, ColumnCoords.FinishColumn, row); // Re-validate Finish on Start change
        break;
      case ColumnCoords.FinishColumn:
        this.triggerValidationOnCell(instance, ColumnCoords.StartColumn, row); // Re-validate Start on Finish change
        break;
    }

    this.hasValidationErrors$.next(
      instance.hasErrors() || this.mandatoryRecords$.value.size > 0
    );
  }

  private onBeforePaste(
    data: Record<string, any>[][],
    worksheet: jspreadsheet.worksheetInstance,
    startCol: number,
    startRow: number
  ): Record<string, any>[][] | false {
    // Check if the pasted data, relative to its starting position, exceeds the worksheet column limit
    const hasExceededColumns =
      data.length > 0 &&
      data[0].length + startCol > worksheet.getColumn().length;

    this.exceededNumberOfColumnsErrors$.next(hasExceededColumns);

    if (hasExceededColumns) {
      this.normalizedPasteData = [];
      return false;
    }

    // Number of inline custom data columns that are affected by the paste action
    const affectedCustomDataCount = data[0].length + startCol - 6;

    if (startCol < ColumnCoords.Location && affectedCustomDataCount > 0) {
      data.forEach((rowData, rowIndex) => {
        const currentActivityValue = worksheet.getValueFromCoords(
          ColumnCoords.Activity,
          startRow + rowIndex
        );

        if (
          currentActivityValue &&
          rowData[ColumnCoords.Activity - startCol] !== currentActivityValue
        ) {
          this.clearCustomDataColumnsForRow(
            worksheet,
            startRow + rowIndex,
            affectedCustomDataCount
          );
        }
      });
    }

    this.normalizedPasteData = this.normalizePasteData(data);
    //TODO(import dpr activities): change logic of normalizedPasteData to match Record<string, any>[][] type,
    // to avoid force casting to unknown, which can generate runtime errors when code tries
    // to access properties or methods expecting that structure
    return this.normalizedPasteData as unknown as Record<string, any>[][];
  }

  private onPaste(
    instance: jspreadsheet.worksheetInstance,
    data: Record<string, any>[]
  ): void {
    this.normalizedPasteData = null;

    // Normalize the casing after paste by updating cell values directly
    data.forEach((cell) => {
      if (!cell.value) return;

      switch (cell.x) {
        case ColumnCoords.ActivityGroup: {
          const matched = findByValue<ActivityGroup>(
            this.data.getActivityGroups(),
            cell.value
          );
          // Ensure matched activity group uses correct casing from dropdown options
          if (matched && matched.name !== cell.value) {
            instance.setValueFromCoords(cell.x, cell.y, matched.name, false);
          }
          break;
        }
        case ColumnCoords.Activity: {
          const pastedActivityGroup = data.find(
            (c) => c.x === ColumnCoords.ActivityGroup && c.y === cell.y
          );
          const activityGroupValue =
            pastedActivityGroup?.value ||
            instance.getValueFromCoords(ColumnCoords.ActivityGroup, cell.y);
          if (!activityGroupValue) break;

          const activityGroup = findByValue<ActivityGroup>(
            this.data.getActivityGroups(),
            activityGroupValue
          );
          if (!activityGroup) break;

          const matched = findByValue<Activity>(
            this.data.getActivities(activityGroup.id),
            cell.value
          );
          // Ensure matched activity uses correct casing from dropdown options
          if (matched && matched.name !== cell.value) {
            instance.setValueFromCoords(cell.x, cell.y, matched.name, false);
          }
          break;
        }
        case ColumnCoords.Location: {
          const pastedActivityGroup = data.find(
            (c) => c.x === ColumnCoords.ActivityGroup && c.y === cell.y
          );
          const pastedActivity = data.find(
            (c) => c.x === ColumnCoords.Activity && c.y === cell.y
          );
          const activityGroupValue =
            pastedActivityGroup?.value ||
            instance.getValueFromCoords(ColumnCoords.ActivityGroup, cell.y);
          const activityValue =
            pastedActivity?.value ||
            instance.getValueFromCoords(ColumnCoords.Activity, cell.y);

          if (!activityGroupValue || !activityValue) break;

          const activityGroup = findByValue<ActivityGroup>(
            this.data.getActivityGroups(),
            activityGroupValue
          );
          if (!activityGroup) break;

          const activity = findByValue<Activity>(
            this.data.getActivities(activityGroup.id),
            activityValue
          );
          if (!activity) break;

          const matched = findByValue<Location>(
            this.data.getLocations(activity.id),
            cell.value
          );
          // Ensure matched location uses correct casing from dropdown options
          if (matched && matched.name !== cell.value) {
            instance.setValueFromCoords(cell.x, cell.y, matched.name, false);
          }
          break;
        }
      }

      // Normalize the casing after paste by updating cell values directly for list values
      const column = instance.getColumn(cell.x);
      const customDataInfo = parseCustomDataInfoFromColumnName(column.name);

      // Only proceed for Boolean or List types
      if (
        customDataInfo?.dataTypeId !== DataType.Boolean &&
        customDataInfo?.dataTypeId !== DataType.List
      ) {
        return;
      }

      const matched = findByValue(
        column.source as (jspreadsheet.DropdownItem | string | number)[],
        cell.value
      );

      if (
        matched &&
        typeof matched === 'object' &&
        'name' in matched &&
        matched.name !== cell.value
      ) {
        instance.setValueFromCoords(cell.x, cell.y, matched.name, false);
      }
    });
  }

  /**
   * After adding new row, the validations need to be attached
   * because this is after the initial setup and the new row won't have it
   */
  private onInsertRow(
    instance: jspreadsheet.worksheetInstance,
    newRow: any
  ): void {
    const validations = this.importDprActivityValidation
      .getValidations(this.data, this.normalizedPasteData)
      .map((value) => ({ value }));

    instance.setValidations(validations);

    // Adjust cell indexes for tracked records after row insertion
    this.mandatoryRecords$.value.forEach((record) => {
      if (record.y >= newRow[0].row) {
        record.y++;
      }
    });
  }

  // Adjust cell indexes for tracked records after row deletion
  private onDeleteRow(
    instance: jspreadsheet.worksheetInstance,
    deletedRows: number[]
  ): void {
    const mandatoryRecords = this.mandatoryRecords$.value;

    // Sort deleted rows to ensure correct index shifting
    const sortedDeleted = [...deletedRows].sort((a, b) => a - b);

    // Create a new Set to avoid mutating while iterating
    const updatedRecords = new Set(
      [...mandatoryRecords]
        .filter((record) => !sortedDeleted.includes(record.y)) // remove deleted rows
        .map((record) => {
          // shift rows after deletions
          const shift = sortedDeleted.filter((row) => row < record.y).length;
          return { ...record, y: record.y - shift };
        })
    );

    this.mandatoryRecords$.next(updatedRecords);

    this.hasValidationErrors$.next(
      instance.hasErrors() || updatedRecords.size > 0
    );
  }

  private onDeleteSelection() {
    const worksheet = this.worksheets[0];
    const selectedCellsByRow = new Map<number, any[]>();

    // Group selected cells by row
    worksheet.getSelected().forEach((cell) => {
      if (!selectedCellsByRow.has(cell.y)) {
        selectedCellsByRow.set(cell.y, []);
      }
      selectedCellsByRow.get(cell.y).push(cell);
    });

    selectedCellsByRow.forEach((cells) => {
      // if selection contains Activity Group or Activity delete read-only as well (since read-only is dependent on these 2 columns)
      if (
        cells.some(
          (c) =>
            c.x === ColumnCoords.ActivityGroup || c.x === ColumnCoords.Activity
        )
      ) {
        cells.forEach((cell) => {
          const cellName = `${jspreadsheet.helpers.getColumnName(cell.x)}${
            cell.y + 1
          }`;
          this.setCellReadonlyState(false, worksheet, cellName);
        });
      } else {
        worksheet.setValue(cells, '');
      }
    });
  }

  /**
   * Intercepts fill-handle operations before changes are applied to block invalid fills (dragging down
   * read-only cells, horizontal drags) and to handle incorrect copy behavior for different column types.
   */
  private onBeforeChanges(
    instance: jspreadsheet.worksheetInstance,
    records: SpreadsheetRecord[],
    origin: string
  ): void {
    if (origin === 'fill-handle' && records.length > 0) {
      // Set flag to prevent onChange from firing during the fill operation
      this.isCellUpdateInProgress = true;
      this.cellsToRestoreAfterFill = [];
      const selection = instance.getSelection();
      const startCol = selection[0];
      const endCol = selection[2] || startCol;
      const startRow = selection[1];
      const endRow = selection[3] || startRow;

      // Prevent performing horizontal fills or dragging down selections that contain only read-only cells
      if (
        this.isHorizontalFill(records, startCol, endCol) ||
        this.areAllCellsReadOnly(
          instance,
          startCol,
          endCol,
          startRow,
          endRow
        ) ||
        !records.some((x) => this.hasValueChanged(instance, x))
      ) {
        records.splice(0, records.length); // Cancel all fills
        this.isCellUpdateInProgress = false;
        return;
      }

      /**
       * Special handling for selections that contain new Activity Group/Activity values:
       * Ensure readonly cells in target rows are made editable (prepared) before fill, so that the
       * new values are copied correctly on drag-down.
       */
      const hasActivityColumns = records.some(
        (r) =>
          r.x === ColumnCoords.ActivityGroup || r.x === ColumnCoords.Activity
      );

      if (hasActivityColumns) {
        this.prepareReadonlyCellsForFill(
          instance,
          records,
          selection,
          startCol,
          endCol,
          endRow
        );
      }

      // Handle fill behavior: ensure values are copied correctly
      records.forEach((record) => {
        record.value = this.handleFillBehavior(
          instance,
          record.value || '',
          record.x,
          record.y,
          selection
        );
      });
    }

    this.preprocessDataBeforeChange(records);

    /**
     * Special handling for custom data columns:
     * - Format percentage values to 2 decimal places
     * - Convert whitespace strings to empty string to avoid displaying 0 or NaN
     * - Apply decimal precision for number columns based on custom data configuration
     */
    records
      .filter((record) => record.value && record.x > ColumnCoords.CommentColumn)
      .forEach((record) => {
        const customDataInfo = parseCustomDataInfoFromColumnName(
          instance.getColumn(record.x).name
        );

        // Convert whitespace to empty string
        if (String.isNullOrWhiteSpace(record.value?.toString())) {
          record.value = '';
          return;
        }

        // Format percentage values: round numbers without decimals, decimals with up to 2 places
        if (customDataInfo?.dataTypeId === DataType.Percentage) {
          const numericValue = String(record.value).replace('%', '');
          if (!isNaN(Number(numericValue))) {
            record.value = (
              Math.floor(parseFloat(numericValue) * 100) / 100
            ).toString();
          }
        }

        // Apply decimal precision for number columns
        if (customDataInfo?.dataTypeId === DataType.Number) {
          const numericValue = Number(record.value);
          if (!isNaN(numericValue)) {
            const customData = this.data.inlineCustomData.find(
              (cd) => cd.id === customDataInfo.customDataId
            );

            const decimalCount = customData?.decimalCount ?? 2;
            const inputDecimalCount = (record.value.split('.')[1] || '').length;
            // Limit the displayed decimals to the minimum between the input and configuration decimal count
            const displayDecimalCount = Math.min(
              inputDecimalCount,
              decimalCount
            );
            const factor = 10 ** decimalCount;
            const roundedValue = Math.floor(numericValue * factor) / factor;

            // If the rounded value is an integer after applying decimal precision, display it without decimals
            record.value = Number.isInteger(roundedValue)
              ? roundedValue.toString()
              : roundedValue.toFixed(displayDecimalCount);
          }
        }
      });
  }

  /**
   * Handles post-processing after fill operations: resets the flag and triggers
   * cascading validations since fill operations don't automatically validate dependent cells.
   */
  private onAfterChanges(
    instance: jspreadsheet.worksheetInstance,
    records: SpreadsheetRecord[],
    origin: string
  ): void {
    if (origin === 'fill-handle') {
      // Reset flag to re-enable onChange for normal user input
      this.isCellUpdateInProgress = false;

      // Restore cell configuration for readonly cells and set the correct value after fill
      this.cellsToRestoreAfterFill.forEach((record) => {
        const cellName = `${jspreadsheet.helpers.getColumnName(record.x)}${
          record.y + 1
        }`;

        // Restore cell configuration for custom data columns (columns after Comment)
        if (record.x > ColumnCoords.CommentColumn) {
          const customData = this.data.inlineCustomData[record.x - 6];
          if (customData) {
            this.restoreCellConfiguration(
              instance,
              customData,
              record.x,
              record.y
            );
          }
        }

        instance.setValue(cellName, record.value || '');
      });
      this.cellsToRestoreAfterFill = [];

      // Re-trigger validations for all affected rows after fill completes
      this.triggerValidationAfterFill(instance, records);

      // Track validation errors after fill
      this.hasValidationErrors$.next(
        instance.hasErrors() || this.mandatoryRecords$.value.size > 0
      );
    }
  }

  /**
   * Checks if all cells in the given selection range are read-only.
   * Returns true if all cells are read-only, false otherwise.
   */
  private areAllCellsReadOnly(
    instance: jspreadsheet.worksheetInstance,
    startCol: number,
    endCol: number,
    startRow: number,
    endRow: number
  ): boolean {
    for (let col = startCol; col <= endCol; col++) {
      for (let row = startRow; row <= endRow; row++) {
        const cellName = `${jspreadsheet.helpers.getColumnName(col)}${row + 1}`;
        if (!instance.isReadOnly(cellName)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Gets the source value that should be copied to a target cell during a fill operation.
   */
  private getSourceValueForFill(
    instance: jspreadsheet.worksheetInstance,
    targetColumn: number,
    targetRow: number,
    selection: number[]
  ): string {
    const startRow = selection[1];
    const endRow = selection[3] || startRow;
    const sourceValues: string[] = [];

    for (let row = startRow; row <= endRow; row++) {
      sourceValues.push(instance.getValueFromCoords(targetColumn, row));
    }

    const patternIndex = (targetRow - endRow - 1) % sourceValues.length;
    return sourceValues[patternIndex] || '';
  }

  /**
   * Method that prepares readonly cells for fill operations.
   *
   * When dragging down rows, jspreadsheet automatically excludes readonly cells from the records array in onBeforeChanges()
   * => readonly custom data cells and Finish cells (for milestones) are not updated
   *
   * This poses an issue when we want to drag down full rows or sections that contain new ActivityGroup/Activity values,
   * and the target rows have readonly cells from their previous configuration.
   * Since jspreadsheet skips readonly cells, handleFillBehavior won't process them, preventing proper updates on drag.
   *
   * Solution:
   * - Make all readonly cells editable before the fill operation
   * - Track them in cellsToRestoreAfterFill to restore their proper state afterwards
   */
  private prepareReadonlyCellsForFill(
    instance: jspreadsheet.worksheetInstance,
    records: SpreadsheetRecord[],
    selection: number[],
    startCol: number,
    endCol: number,
    endRow: number
  ): void {
    const targetRows = new Set(
      records.filter((r) => r.y > endRow).map((r) => r.y)
    );
    const columnsInRange = [
      ColumnCoords.FinishColumn,
      ...this.data.inlineCustomData.map((_, index) => index + 6),
    ].filter((col) => col >= startCol && col <= endCol);

    targetRows.forEach((row) => {
      columnsInRange.forEach((col) => {
        const cellName = `${jspreadsheet.helpers.getColumnName(col)}${row + 1}`;

        if (instance.isReadOnly(cellName)) {
          this.setCellReadonlyState(false, instance, cellName);
          this.cellsToRestoreAfterFill.push({
            x: col,
            y: row,
            value:
              this.getSourceValueForFill(instance, col, row, selection) || '',
          });
        }
      });
    });
  }

  /**
   * Detects if a fill-handle operation is attempting to fill horizontally across columns.
   *
   * We block horizontal fills because different columns have different data types and
   * validation rules.
   */
  private isHorizontalFill(
    records: SpreadsheetRecord[],
    startCol: number,
    endCol: number
  ): boolean {
    // If any record falls outside the source column, it's a horizontal fill
    return records.some((record) => record.x < startCol || record.x > endCol);
  }

  /**
   * Customizes fill-handle behavior based on column type.
   *
   * Different column types require different fill behaviors:
   * 1. Columns ActivityGroup, Activity, Location, Start, Finish:
   * - Copied correctly by default by jspreadsheet, so no changes are needed
   *
   * 2. Comment & Custom data columns:
   * - Repeat the source pattern to prevent auto-increment behavior
   */
  private handleFillBehavior(
    instance: jspreadsheet.worksheetInstance,
    recordValue: string,
    targetColumn: number,
    targetRow: number,
    selection: number[]
  ): string {
    // Columns before Comment: use default jspreadsheet behavior
    if (targetColumn < ColumnCoords.CommentColumn) {
      return recordValue;
    }

    const customData = this.data.inlineCustomData[targetColumn - 6];
    const customDataType = this.getCustomDataType(customData?.dataTypeId);

    // Handle comment and inline custom data columns: repeat pattern to prevent auto-increment
    if (
      targetColumn === ColumnCoords.CommentColumn ||
      ['text', 'dropdown'].includes(customDataType)
    ) {
      const sourceValue = this.getSourceValueForFill(
        instance,
        targetColumn,
        targetRow,
        selection
      );

      // If source value is readonly, restore its configuration and keep it empty
      if (sourceValue === readOnlyPlaceholder) {
        this.cellsToRestoreAfterFill.push({
          x: targetColumn,
          y: targetRow,
          value: '',
        });
        return '';
      }

      return sourceValue;
    }

    return recordValue;
  }

  /**
   * Programmatically triggers validation on a specific cell.
   *
   * Some validations depend on values in other cells (ex. Activity depends on ActivityGroup).
   * When those dependencies change, we need to re-validate dependent cells even if their displayed
   * value hasn't changed. Setting the value to itself triggers the validation pipeline.
   *
   * The isCellUpdateInProgress flag prevents this programmatic update from triggering
   * onChange handlers, which would cause infinite validation loops.
   */
  private triggerValidationOnCell(
    instance: jspreadsheet.worksheetInstance,
    column: number,
    row: number
  ): void {
    const cellValue = instance.getValueFromCoords(column, row);

    if (cellValue && cellValue.toString().trim() !== '') {
      // Set validation flag to prevent infinite loops
      this.isCellUpdateInProgress = true;

      try {
        instance.setValueFromCoords(column, row, cellValue, true);
      } finally {
        this.isCellUpdateInProgress = false;
      }
    }
  }

  private preprocessDataBeforeChange(records: SpreadsheetRecord[]): void {
    for (const record of records) {
      if (this.preprocessorFunctions.has(+record.x)) {
        record.value = this.preprocessorFunctions
          .get(+record.x)
          .reduce((value, fn) => fn(value), record.value || '');
      }
    }
  }

  /**
   * Triggers cascading validations for all rows affected by a fill-handle operation.
   *
   * Steps:
   * 1. Identify all the rows that were modified by the fill operation
   * 2. For each row, determine which columns were filled
   * 3. Trigger dependent validations based on column relationships:
   * - ActivityGroup changes => revalidate Activity + Location
   * - Activity changes => revalidate Location
   * - Start time changes => revalidate Finish time
   * - Finish time changes => revalidate Start time
   */
  private triggerValidationAfterFill(
    instance: jspreadsheet.worksheetInstance,
    records: SpreadsheetRecord[]
  ): void {
    // Get unique rows affected by the fill operation
    const affectedRows = new Set(records.map((record) => record.y));

    affectedRows.forEach((row) => {
      // Determine which columns were modified on this specific row
      const affectedColumns = records
        .filter((record) => record.y === row)
        .map((record) => record.x);

      // Cascade validations based on column dependencies
      // If Activity Group changes, re-validate Activity and Location
      if (affectedColumns.includes(ColumnCoords.ActivityGroup)) {
        this.handleActivityGroupChange(instance, row);
        this.updateMilestoneFinishColumn(instance, row);
        this.triggerValidationOnCell(instance, ColumnCoords.Activity, row);
        this.triggerValidationOnCell(instance, ColumnCoords.Location, row);
      }

      // If Activity changes, re-validate Location
      if (affectedColumns.includes(ColumnCoords.Activity)) {
        this.handleActivityChange(instance, row);
        this.updateMilestoneFinishColumn(instance, row);
        this.triggerValidationOnCell(instance, ColumnCoords.Location, row);
      }

      // If Start or Finish time changes, re-validate the other
      if (affectedColumns.includes(ColumnCoords.StartColumn)) {
        this.updateMilestoneFinishColumn(instance, row);
        this.triggerValidationOnCell(instance, ColumnCoords.FinishColumn, row);
      }

      if (affectedColumns.includes(ColumnCoords.FinishColumn)) {
        this.triggerValidationOnCell(instance, ColumnCoords.StartColumn, row);
      }
    });
  }

  private filterActivityOptions(
    worksheet: jspreadsheet.worksheetInstance,
    y: number,
    config: jspreadsheet.Column
  ): jspreadsheet.Column {
    const activityGroupName = worksheet.getValueFromCoords(
      ColumnCoords.ActivityGroup,
      y
    );
    const activityGroup = findByValue<ActivityGroup>(
      this.data.getActivityGroups(),
      activityGroupName
    );

    if (!activityGroup) {
      config.source = [this.emptyDropdownItem];
      return config;
    }

    const activityOptions = this.data
      .getActivities(activityGroup.id)
      .map((x) => x.name);

    config.source = this.setDropdownSource(activityOptions);
    config.autocomplete = true;

    return config;
  }

  private filterLocationOptions(
    worksheet: jspreadsheet.worksheetInstance,
    y: number,
    config: jspreadsheet.Column
  ): jspreadsheet.Column {
    const activityGroupName = worksheet.getValueFromCoords(
      ColumnCoords.ActivityGroup,
      y
    );
    const activityName = worksheet.getValueFromCoords(ColumnCoords.Activity, y);

    const activityGroup = findByValue<ActivityGroup>(
      this.data.getActivityGroups(),
      activityGroupName
    );

    if (!activityGroup || !activityName) {
      config.source = [this.emptyDropdownItem];
      return config;
    }

    const availableActivities = this.data.getActivities(activityGroup.id);
    const selectedActivity = availableActivities.find(
      (activity) => activity.name === activityName
    );

    if (!selectedActivity) {
      config.source = [this.emptyDropdownItem];
      return config;
    }

    const locationOptions = this.data
      .getLocations(selectedActivity.id)
      .map((x) => x.name);

    config.source = this.setDropdownSource(locationOptions);
    config.autocomplete = true;

    return config;
  }

  private clearValidationState(instance: jspreadsheet.worksheetInstance): void {
    this.mandatoryRecords$.value.forEach((record) =>
      this.resetCellStyle(instance, record)
    );
    this.mandatoryRecords$.next(new Set());
  }

  private resetCellStyle(
    instance: jspreadsheet.worksheetInstance,
    record: { x: number; y: number }
  ): void {
    const cellName = jspreadsheet.helpers.getColumnNameFromCoords(
      record.x,
      record.y
    );
    instance.resetStyle(cellName);
  }

  private highlightInvalidCell(
    instance: jspreadsheet.worksheetInstance,
    column: number,
    row: number
  ): void {
    instance.setStyle([{ x: column, y: row }], 'background-color', '#ffefef');
  }

  private isMandatoryDataValid(data: Record<string, any>[]): boolean {
    const mandatoryColumns = [
      { key: 'ActivityGroup', index: ColumnCoords.ActivityGroup },
      { key: 'Activity', index: ColumnCoords.Activity },
      { key: 'Location', index: ColumnCoords.Location },
      { key: 'Start', index: ColumnCoords.StartColumn },
      { key: 'Finish', index: ColumnCoords.FinishColumn },
    ];

    let hasErrors = false;

    data.forEach((row, rowIndex) => {
      // Skip empty rows
      const rowValues = Array.isArray(row) ? row : Object.values(row);
      if (!rowValues.some((cell) => cell?.toString().trim())) {
        return;
      }

      // Validate mandatory fields
      mandatoryColumns.forEach(({ key, index }) => {
        const cellValue = Array.isArray(row) ? row[index] : row[key];
        if (!cellValue?.toString().trim()) {
          this.highlightInvalidCell(this.worksheets[0], index, rowIndex);
          this.mandatoryRecords$.value.add({ x: index, y: rowIndex });
          hasErrors = true;
        }
      });
    });

    return !this.worksheets[0].hasErrors() && !hasErrors;
  }

  private handleActivityGroupChange(
    instance: jspreadsheet.worksheetInstance,
    row: number
  ): void {
    const currentActivityValue = instance.getValueFromCoords(
      ColumnCoords.Activity,
      row
    );

    // Clear activity if it's not valid for the selected activity group
    if (
      !this.isActivityValidForActivityGroup(instance, row, currentActivityValue)
    ) {
      const activityColumnName = jspreadsheet.helpers.getColumnNameFromCoords(
        ColumnCoords.Activity,
        row
      );

      instance.setValue(activityColumnName, '');
    }

    this.makeCustomDataReadOnlyForRow(instance, row);
  }

  private clearCustomDataColumnsForRow(
    instance: jspreadsheet.worksheetInstance,
    row: number,
    columnCount?: number
  ): void {
    this.data.inlineCustomData
      .slice(0, columnCount)
      .forEach((customData, index) => {
        const columnIndex = index + 6; // Inline custom data starts at column 6
        const cellName = `${jspreadsheet.helpers.getColumnName(columnIndex)}${
          row + 1
        }`;

        if (this.getCustomDataType(customData.dataTypeId) !== 'text') {
          this.restoreCellConfiguration(instance, customData, columnIndex, row);
        }
        this.setCellReadonlyState(false, instance, cellName);
      });
  }

  private handleActivityChange(
    instance: jspreadsheet.worksheetInstance,
    row: number
  ): void {
    this.makeCustomDataReadOnlyForRow(instance, row);

    const currentLocationValue = instance.getValueFromCoords(
      ColumnCoords.Location,
      row
    );

    const locationColumnName = jspreadsheet.helpers.getColumnNameFromCoords(
      ColumnCoords.Location,
      row
    );

    // Clear location if it's not valid for the selected activity
    if (!this.isLocationValidForActivity(instance, row, currentLocationValue)) {
      instance.setValue(locationColumnName, '');
    }
  }

  private updateMilestoneFinishColumn(
    instance: jspreadsheet.worksheetInstance,
    row: number
  ): void {
    const selectedActivity = this.getSelectedActivity(instance, row);
    const finishColumnName = jspreadsheet.helpers.getColumnNameFromCoords(
      ColumnCoords.FinishColumn,
      row
    );

    // If the activity is a milestone, set finish time to match start time
    if (selectedActivity?.isMilestone) {
      // For milestones, sync finish time with start time and make it read-only
      const startTime = instance.getValueFromCoords(
        ColumnCoords.StartColumn,
        row
      );

      this.setCellReadonlyState(true, instance, finishColumnName, startTime);
    } else {
      // For non-milestones, make finish column editable but preserve existing value
      const finishTime = instance.getValueFromCoords(
        ColumnCoords.FinishColumn,
        row
      );

      this.setCellReadonlyState(false, instance, finishColumnName, finishTime);
    }
  }

  private isActivityValidForActivityGroup(
    instance: jspreadsheet.worksheetInstance,
    row: number,
    activityValue: string
  ): boolean {
    const activityGroupName = instance.getValueFromCoords(
      ColumnCoords.ActivityGroup,
      row
    );
    const activityGroup = findByValue<ActivityGroup>(
      this.data.getActivityGroups(),
      activityGroupName
    );

    if (!activityGroup) {
      return false;
    }

    const availableActivities = this.data.getActivities(activityGroup.id);
    return (
      availableActivities?.some(
        (activity) => activity.name === activityValue
      ) ?? false
    );
  }

  private isLocationValidForActivity(
    instance: jspreadsheet.worksheetInstance,
    row: number,
    locationValue: string
  ): boolean {
    const selectedActivity = this.getSelectedActivity(instance, row);
    if (!selectedActivity) {
      return false;
    }

    const availableLocations = this.data.getLocations(selectedActivity.id);
    return availableLocations.some(
      (location) => location.name === locationValue
    );
  }

  private customRenderer(cd: CustomData): jspreadsheet.Column['render'] {
    return cd.dataTypeId === DataType.Percentage
      ? this.customPercentageRender
      : '';
  }

  private formatDateTime(value: string): string {
    if (this.isEmptyOrPlaceholder(value)) return value;

    const matchResult = value.match(dateTimeRegex);
    if (!matchResult) return value;

    const dateTime = this.parseDateTime(matchResult, true);
    if (!dateTime || !this.isValidDateTime(dateTime, matchResult, true))
      return value;

    const date = convertToDateTimeWithoutTimeZoneSuffix(
      dateTime,
      dateTime.getHours(),
      dateTime.getMinutes()
    );
    return this.datePipe.transform(date, 'dd-MM-yyyy HH:mm');
  }

  private formatDate(value: string): string {
    if (this.isEmptyOrPlaceholder(value)) return value;

    const matchResult = value.match(dateRegex);
    if (!matchResult) return value;

    const date = this.parseDateTime(matchResult, false);
    if (!date || !this.isValidDateTime(date, matchResult, false)) return value;

    return this.datePipe.transform(
      convertToDateTimeWithoutTimeZoneSuffix(date),
      'dd-MM-yyyy'
    );
  }

  private isEmptyOrPlaceholder(value: string): boolean {
    return String.isNullOrWhiteSpace(value) || value === readOnlyPlaceholder;
  }

  private parseDateTime(
    matchResult: RegExpMatchArray,
    includeTime: boolean
  ): Date | null {
    const day = parseInt(matchResult[1], 10);
    const month = parseInt(matchResult[2], 10) - 1;
    const year = parseInt(matchResult[3], 10);

    if (includeTime) {
      const hour = parseInt(matchResult[4], 10);
      const minute = parseInt(matchResult[5], 10);
      return new Date(year, month, day, hour, minute);
    }

    return new Date(year, month, day);
  }

  private isValidDateTime(
    date: Date,
    matchResult: RegExpMatchArray,
    includeTime: boolean
  ): boolean {
    const day = parseInt(matchResult[1], 10);
    const month = parseInt(matchResult[2], 10) - 1;

    if (
      date.getMonth() !== month ||
      date.getDate() !== day ||
      isDateBeforeEpoch(date)
    ) {
      return false;
    }

    if (includeTime) {
      const hour = parseInt(matchResult[4], 10);
      const minute = parseInt(matchResult[5], 10);
      return date.getHours() === hour && date.getMinutes() === minute;
    }

    return true;
  }

  private customPercentageRender = (td: HTMLElement): void => {
    if (!td?.innerText) {
      return;
    }

    const numericValue = td.innerText.replace('%', '');
    if (!isNaN(Number(numericValue))) {
      td.innerText =
        (Math.floor(parseFloat(numericValue) * 100) / 100).toString() + '%';
    }
  };

  private getSelectedActivity(
    instance: jspreadsheet.worksheetInstance,
    row: number
  ): Activity | null {
    const activityGroupName = instance.getValueFromCoords(
      ColumnCoords.ActivityGroup,
      row
    );
    const activityName = instance.getValueFromCoords(
      ColumnCoords.Activity,
      row
    );

    const activityGroup = findByValue<ActivityGroup>(
      this.data.getActivityGroups(),
      activityGroupName
    );
    if (!activityGroup) {
      return null;
    }

    return findByValue<Activity>(
      this.data.getActivities(activityGroup.id),
      activityName
    );
  }

  private getCustomDataType(customDataType: DataType): 'text' | 'dropdown' {
    switch (customDataType) {
      case DataType.List:
      case DataType.Boolean:
        return 'dropdown';
      default:
        return 'text';
    }
  }

  private makeCustomDataReadOnlyForRow(
    instance: jspreadsheet.worksheetInstance,
    row: number
  ): void {
    const selectedActivity = this.getSelectedActivity(instance, row);
    if (!selectedActivity) {
      // If the activity is deleted, reset all custom data cells that were previously read-only
      this.clearCustomDataColumnsForRow(instance, row);

      return;
    }

    // Get the active custom data for the selected activity
    const activeCustomDataIds = this.getActiveCustomDataForActivity(
      selectedActivity.id
    );

    // Make the columns read-only if the custom data is not active for the selected activity
    this.data.inlineCustomData.forEach((customData, index) => {
      const columnIndex = index + 6; // Inline custom data starts at column 6
      const cellName = `${jspreadsheet.helpers.getColumnName(columnIndex)}${
        row + 1
      }`;
      const originalType = this.getCustomDataType(customData.dataTypeId);
      const wasReadonly = instance.isReadOnly(cellName);

      if (!activeCustomDataIds.includes(customData.id)) {
        // Change cell type to text temporarily to display "-" properly
        originalType !== 'text' &&
          instance.setProperty(columnIndex, row, { type: 'text' });

        this.setCellReadonlyState(
          true,
          instance,
          cellName,
          readOnlyPlaceholder
        );

        return;
      }

      // Only restore configuration if the cell was readonly and not originally a text type
      if (!wasReadonly) {
        return;
      }

      originalType !== 'text' &&
        this.restoreCellConfiguration(instance, customData, columnIndex, row);

      this.setCellReadonlyState(false, instance, cellName);
    });
  }

  private getActiveCustomDataForActivity(activityId: number): number[] {
    return this.data.activityMobilisationList
      .filter((am) => {
        return am.activityId === activityId && am.isActive;
      })
      .map((am) => am.customDataId);
  }

  /*
   * Restore initial cell configuration for custom data cells that have been temporarily converted
   * to text type in order to display the read-only placeholder '─'.
   * Dropdowns also need their source options restored to function properly.
   * Percentage columns need their custom renderer restored to display the '%' symbol.
   */
  private restoreCellConfiguration(
    instance: jspreadsheet.worksheetInstance,
    customData: CustomData,
    column: number,
    row: number
  ): void {
    const cellType = this.getCustomDataType(customData.dataTypeId);
    const cellConfig: CellConfig = {
      type: cellType,
      autoCasting: ![DataType.FreeText, DataType.Number].includes(
        customData.dataTypeId
      ),
    };

    if (cellType === 'dropdown') {
      cellConfig.source = this.setDropdownSource(
        this.data.getSelectionOptions(customData)
      );
      cellConfig.autocomplete = true;
    }

    // Restore custom renderer for percentage columns to ensure '%' symbol is always displayed
    cellConfig.render = this.customRenderer(customData);

    instance.setProperty(column, row, cellConfig);
  }

  private normalizePasteData(data: any[][]): string[][] {
    return data.map((row) =>
      row.map((cell) => {
        // If it's in object format (from jspreadsheet), extract the value
        if (typeof cell === 'object' && cell !== null && 'value' in cell) {
          return cell.value?.toString().trim() || '';
        }

        // If it's already a string (ex: from Excel), return as is
        if (typeof cell === 'string') {
          return cell.trim();
        }

        // Handle other types (numbers, dates, etc.)
        return cell?.toString().trim() || '';
      })
    );
  }

  private isEmpty(value: any): boolean {
    if (value === null) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    return false;
  }

  private convertRowToGridData(
    row: Record<string, any>
  ): DprActivityGridData | null {
    const activityGroup = findByValue<ActivityGroup>(
      this.data.getActivityGroups(),
      row.ActivityGroup
    );
    const activity = findByValue<Activity>(
      this.data.getActivities(activityGroup?.id),
      row.Activity
    );
    const location = findByValue<Location>(
      this.data.getLocations(activity?.id),
      row.Location
    );

    if (!activityGroup || !activity || !location) {
      return null;
    }

    const gridData: DprActivityGridData = {
      id: uuid(),
      activityGroupId: activityGroup.id,
      activityId: activity.id,
      locationId: location.id,
      comment: row.Comment?.toString() || '',
      start: normalizeTimeFormat(row.Start),
      finish: normalizeTimeFormat(row.Finish),
      dprDetailId: null,
      parallelActivityType: this.data.parallelActivityType,
      dprSubActivities: [],
      dprActivityCustomDataList: [],
      dprActivityAttachments: [],
      canBeDeleted: true,
    };

    // Process inline custom data
    if (this.data.inlineCustomData.length === 0) {
      return gridData;
    }

    const activeCustomData = this.data.inlineCustomData.filter((cd) =>
      this.getActiveCustomDataForActivity(activity.id).includes(cd.id)
    );
    activeCustomData.forEach((customData) => {
      const cellValue = row[buildCustomDataColumnName(customData)];

      if (!this.isEmpty(cellValue)) {
        gridData.dprActivityCustomDataList.push({
          id: uuid(),
          dprActivityId: gridData.id,
          customDataId: customData.id,
          value: this.getGridDataFormatForValueType(
            cellValue,
            customData.dataTypeId
          ),
          comment: '',
        });
      }
    });

    const copyOptions = this.dprActivityCopyService.getOptions(gridData);
    return this.dprActivityCopyService.copy(gridData, copyOptions);
  }

  private getGridDataFormatForValueType(
    value: string,
    dataType: DataType
  ): string {
    switch (dataType) {
      case DataType.Date:
        return this.convertToGridDataFormat(value, false);
      case DataType.Datetime:
        return this.convertToGridDataFormat(value, true);
      default:
        return value;
    }
  }

  private convertToGridDataFormat(
    value: string,
    includesTime: boolean
  ): string {
    if (this.isEmptyOrPlaceholder(value)) {
      return null;
    }

    const matchResult = value.match(includesTime ? dateTimeRegex : dateRegex);
    if (!matchResult) return null;

    const dateTime = this.parseDateTime(matchResult, includesTime);

    if (includesTime) {
      return convertToDateTimeWithoutTimeZoneSuffix(
        dateTime,
        dateTime.getHours(),
        dateTime.getMinutes()
      );
    }

    return convertToDateTimeWithoutTimeZoneSuffix(dateTime);
  }

  /**
   * Observes DOM changes to sync max-width with min-width for the dynamically modified .jss_input element.
   * This is needed to ensure that the text inputs do not cross the viewport boundary.
   */
  private observeJssInputs(): void {
    this.mutationObserver = new MutationObserver(() => {
      const jssInput =
        this.jspreadsheet.nativeElement.querySelector('.jss_input');

      const computedStyle = window.getComputedStyle(jssInput);
      const minWidth = computedStyle.minWidth;

      if (minWidth && minWidth !== 'none' && minWidth !== '0px') {
        jssInput.style.maxWidth = minWidth;
      }
    });

    this.mutationObserver.observe(this.jspreadsheet.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.jspreadsheet?.nativeElement) {
        this.worksheets[0].setViewport(
          this.jspreadsheet.nativeElement.offsetWidth,
          this.jspreadsheet.nativeElement.offsetHeight
        );

        const tableWidth = this.jspreadsheet.nativeElement.offsetWidth;

        const width =
          tableWidth > 1888
            ? (tableWidth - 368) / (4 + this.data.inlineCustomData.length)
            : 1520 / (4 + this.data.inlineCustomData.length);

        const customDataColumns = Array.from(
          { length: this.data.inlineCustomData.length },
          (_, i) => i + 6
        );

        this.worksheets[0].setWidth([0, 1, 2, ...customDataColumns], width);
        this.worksheets[0].setWidth(5, width + 140);
      }
    });

    this.resizeObserver.observe(this.jspreadsheet?.nativeElement);
  }

  /**
   * Custom Read-Only Implementation
   *
   * We use background color instead of jspreadsheet's native `isReadOnly()` because
   * the library's validation runs immediately after value changes, before the cell
   * can be marked read-only. This timing issue causes `isReadOnly()` to return NULL
   * during validation, making it unreliable.
   *
   * Implementation:
   * - `setCellReadonlyMode()`: Sets cell read-only state and applies background styling
   * - `cellIsReadonly()`: Checks background color to determine read-only status
   * - Validation logic uses background color check instead of `isReadOnly()`
   *
   * This approach ensures deterministic validation independent of jspreadsheet's
   * internal timing and provides clear visual feedback to users.
   */
  private setCellReadonlyState(
    makeReadonly: boolean,
    worksheet: jspreadsheet.worksheetInstance,
    cellName: string,
    value: string = ''
  ): void {
    if (makeReadonly) {
      worksheet.setStyle(cellName, 'background-color', readOnlyBackgroundColor);
      worksheet.setReadOnly(cellName, true);
    } else {
      worksheet.resetStyle(cellName);
      worksheet.setReadOnly(cellName, false);
    }

    worksheet.setValue(cellName, value, true);
  }

  private hasValueChanged(
    instance: jspreadsheet.worksheetInstance,
    record: SpreadsheetRecord
  ): boolean {
    const currentValue = instance.getValueFromCoords(record.x, record.y);
    const normalizedCurrentValue = String.isNullOrWhiteSpace(
      currentValue?.toString()
    )
      ? ''
      : currentValue;

    return normalizedCurrentValue !== record.value;
  }

  private setDropdownSource(
    items: string[] | SelectionItem[] | jspreadsheet.DropdownItem[]
  ): string[] | SelectionItem[] | jspreadsheet.DropdownItem[] {
    return items.length ? items : [this.emptyDropdownItem];
  }
}
